import { Spinner, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetProcessing, DatasetProcessingAxisState } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import {
  DATASET_PROCESSING_AXES,
  DATASET_PROCESSING_AXIS_LABELS,
  DATASET_PROCESSING_STATE_LABELS,
  datasetProcessingTone,
  type DatasetProcessingTone,
} from "@/components/dataset/datasetProcessing";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import styles from "./DatasetOverviewPanel.module.css";

/**
 * 파생 처리 상태 카드. 업로드가 데이터셋을 공개한 뒤에도 계속 실행되는 산출(에피소드 진단)의
 * 진행 상태를 보여 준다.
 *
 * **이 카드는 데이터셋의 사용 가능 여부를 표시하지 않는다.** 이 상태는 catalog status 와 독립이다.
 * 여기 값과 관계없이 데이터셋은 이미 목록에 있고, 산출 중에도 학습·다운로드가 가능하다.
 *
 * Owns: 축별 상태의 표시.
 * Does not own: 어휘(`components/dataset/datasetProcessing.ts`) · 진단 결과 자체
 * (에피소드/파일 탭의 Parquet 미리보기).
 */
export function DatasetProcessingCard({
  processing,
}: {
  processing: DatasetProcessing | null;
}): ReactNode {
  return (
    <section className={styles.processing} aria-label="처리 상태">
      <Text variant={hatcheryTextModes["caption-bold"]}>처리 상태</Text>
      {processing === null ? (
        <p className={`${styles.muted} ${styles.loadingLine}`}>
          <Spinner size="sm" className={inlineSpinnerClassName} />
          처리 상태를 불러오고 있습니다.
        </p>
      ) : (
        <dl className={styles.processingList}>
          {DATASET_PROCESSING_AXES.map((axis) => (
            <div key={axis} className={styles.processingRow}>
              <dt className={styles.processingAxis}>{DATASET_PROCESSING_AXIS_LABELS[axis]}</dt>
              <dd className={stateClassName(processing[axis])}>
                {DATASET_PROCESSING_STATE_LABELS[processing[axis]]}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

const TONE_CLASS_NAMES: Record<DatasetProcessingTone, string> = {
  positive: styles.tonePositive,
  progress: styles.toneProgress,
  danger: styles.toneDanger,
  muted: styles.toneMuted,
};

function stateClassName(state: DatasetProcessingAxisState): string {
  return `${styles.processingState} ${TONE_CLASS_NAMES[datasetProcessingTone(state)]}`;
}
